/**
 * AQI history — the daily snapshot collection (`aqiHistory`) behind the
 * Dashboard trend sparkline.
 *
 * WHY this exists: the AQI dial shows today's score, but a single number
 * can't tell you whether the team is climbing or sliding. Persisting one
 * honest point per day turns the dial into a scoreboard with a direction.
 *
 * WHY one row per day (id === local date key): the score recomputes on
 * every data change, so a naive log would be noise. Keying the record by
 * the local calendar date and letting the latest reading of the day win
 * collapses that into exactly one trustworthy point per day.
 *
 * Honest accounting: like everything else this is deterministic and
 * computed from live store data; in mock mode it lives in localStorage
 * and flips to Firestore with the same flag as the rest of the app.
 */

import { db, nowIso } from './store';
import type { AqiSnapshot } from './types';
import type { AqiResult } from '../crm/aqi';

/** Local calendar date key (YYYY-MM-DD) — the per-day snapshot id. */
export function aqiDateKey(now: number = Date.now()): string {
  const d = new Date(now);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Persist today's AQI as a daily snapshot. Idempotent per local date: the
 * latest reading of the day overwrites earlier ones, and an unchanged
 * score is a no-op so we never churn subscriptions. Failures are
 * swallowed — the trend is a nice-to-have and must never break the
 * Dashboard render that feeds it.
 */
export async function recordAqiSnapshot(
  aqi: AqiResult,
  now: number = Date.now(),
): Promise<void> {
  try {
    const date = aqiDateKey(now);
    const components = aqi.components.map((c) => ({ key: c.key, score: c.score }));
    const all = await db.list('aqiHistory');
    const existing = all.find((s) => s.date === date);
    if (existing) {
      if (existing.score === aqi.score) return; // no movement → no write
      await db.update('aqiHistory', existing.id, {
        score: aqi.score,
        components,
        updatedAt: nowIso(),
      });
    } else {
      await db.add('aqiHistory', {
        id: date,
        date,
        score: aqi.score,
        components,
        updatedAt: nowIso(),
      });
    }
  } catch {
    /* trend is a nice-to-have; never break the dashboard */
  }
}

/** All snapshots, oldest first. */
export async function listAqiHistory(): Promise<AqiSnapshot[]> {
  const all = await db.list('aqiHistory');
  return [...all].sort((a, b) => a.date.localeCompare(b.date));
}

/** Subscribe to the snapshot history (oldest first). */
export function subscribeAqiHistory(fn: (rows: AqiSnapshot[]) => void): () => void {
  return db.subscribe('aqiHistory', (rows) =>
    fn([...rows].sort((a, b) => a.date.localeCompare(b.date))),
  );
}
